import { addYears } from '@if/core/date'
import { EApplicantType, getEGenderList, getEMaritalStatusList, getERelationToApplicantList } from '@if/core/types'
import {
  ControlledCheckbox,
  ControlledPhoneField,
  ControlledSelect,
  ControlledSinField,
  ControlledDateOnlyPicker,
  ControlledTextField,
  FieldGrid,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Stack } from '@mui/material'
import { getLanguageOrder } from '@src/data/language-selectors'
import { useProfileLanguage } from '@src/hooks/language-hooks'
import { Constants } from '@src/types/Constants'
import { ApplicantDto, CreditApplication } from '@src/types/CreditApplicationSchema'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  applicantType: EApplicantType
  formControl: Control<CreditApplication | ApplicantDto>
  hasLegalName: boolean
  hasPrequalifyBeenPressed: boolean
  disableEdit: boolean
  isSinNeeded: boolean | null
}

const ApplicantInformationParameters = ({
  applicantType,
  formControl,
  hasLegalName,
  disableEdit,
  isSinNeeded,
}: Props) => {
  const { t } = useTranslation()
  const profileLanguage = useProfileLanguage()

  const genderList = useTranslatedOptionEnum('EGender', getEGenderList(), 'enum')
  const maritalStatusList = useTranslatedOptionEnum('EMaritalStatus', getEMaritalStatusList(), 'enum')
  const languageList = useTranslatedOptionEnum('ELanguage', getLanguageOrder(profileLanguage), 'enum')
  const relationToApplicantList = useTranslatedOptionEnum('ERelationToApplicant', getERelationToApplicantList(), 'enum')

  const isCoApplicant = applicantType !== EApplicantType.Applicant

  return (
    <Stack spacing={3}>
      <FieldGrid columns={{ xs: 1, sm: 2, lg: 4 }}>
        <ControlledTextField
          id="applicantFirstName"
          name={`${applicantType}.firstName`}
          control={formControl}
          label={t('editCreditApplication.information.firstName')}
          slotProps={{ htmlInput: { maxLength: 50 } }}
          fullWidth
          disabled={disableEdit}
        />
        <ControlledTextField
          id="applicantMiddleName"
          name={`${applicantType}.middleName`}
          control={formControl}
          label={t('editCreditApplication.information.middleName')}
          slotProps={{ htmlInput: { maxLength: 50 } }}
          fullWidth
          disabled={disableEdit}
        />
        <ControlledTextField
          id="applicantLastName"
          name={`${applicantType}.lastName`}
          control={formControl}
          label={t('editCreditApplication.information.lastName')}
          slotProps={{ htmlInput: { maxLength: 50 } }}
          fullWidth
          disabled={disableEdit || hasLegalName}
        />
        <ControlledCheckbox
          name={`${applicantType}.hasLegalName`}
          control={formControl}
          label={t('editCreditApplication.information.hasLegalName')}
          disabled={disableEdit}
        />
        <ControlledDateOnlyPicker
          name={`${applicantType}.birthDate`}
          control={formControl}
          label={t('editCreditApplication.information.birthDate')}
          minDate={addYears(new Date(), -Constants.MaxYearsToDisplay)}
          maxDate={new Date()}
          fullWidth
          disabled={disableEdit}
        />
        <ControlledSinField
          name={`${applicantType}.sin`}
          control={formControl}
          label={t('editCreditApplication.information.sin')}
          fullWidth
          disabled={disableEdit && !isSinNeeded}
        />
        <ControlledSelect
          name={`${applicantType}.genderId`}
          control={formControl}
          options={genderList}
          label={t('editCreditApplication.information.genderId')}
          disableEmptyOption
          fullWidth
          disabled={disableEdit}
        />
        <ControlledSelect
          name={`${applicantType}.maritalStatusId`}
          control={formControl}
          options={maritalStatusList}
          label={t('editCreditApplication.information.maritalStatusId')}
          disableEmptyOption
          fullWidth
          disabled={disableEdit}
        />
        <ControlledSelect
          name={`${applicantType}.languageId`}
          control={formControl}
          options={languageList}
          label={t('editCreditApplication.information.languageId')}
          disableEmptyOption
          fullWidth
          disabled={disableEdit}
        />
        {isCoApplicant && (
          <ControlledSelect
            name={`${applicantType}.relationWithApplicant`}
            control={formControl}
            options={relationToApplicantList}
            label={t('editCreditApplication.information.relationWithApplicant')}
            fullWidth
          />
        )}
      </FieldGrid>

      <FieldGrid columns={{ xs: 1, md: 2 }}>
        <ControlledPhoneField
          id="cellPhone"
          name={`${applicantType}.cellPhone`}
          control={formControl}
          label={t('editCreditApplication.information.cellPhone')}
          fullWidth
          disabled={disableEdit}
        />
        <ControlledPhoneField
          id="homePhone"
          name={`${applicantType}.homePhone`}
          control={formControl}
          label={t('editCreditApplication.information.homePhone')}
          fullWidth
          disabled={disableEdit}
        />
        <ControlledTextField
          id="email"
          type="email"
          name={`${applicantType}.email`}
          control={formControl}
          label={t('editCreditApplication.information.email')}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 60 } }}
          span="full"
          disabled={disableEdit}
        />
      </FieldGrid>
    </Stack>
  )
}
export default ApplicantInformationParameters
